/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as log from "loglevel";
import {isEmptyable} from "../util/Emptyable";
import {UiRelativeWorkSpaceViewPosition} from "../../generated/UiRelativeWorkSpaceViewPosition";
import {UiPanel} from "../UiPanel";
import {UiToolbarConfig} from "../../generated/UiToolbarConfig";
import {UiWorkSpaceLayoutViewConfig} from "../../generated/UiWorkSpaceLayoutViewConfig";
import {UiComponentConfig} from "../../generated/UiComponentConfig";
import {UiWorkSpaceLayout, UiWorkspaceLayoutDndDataTransfer} from "./UiWorkSpaceLayout";
import * as $ from "jquery";
import {UiSplitDirection} from "../../generated/UiSplitDirection";
import {UiSplitSizePolicy} from "../../generated/UiSplitSizePolicy";
import {generateUUID, getMicrosoftBrowserVersion} from "../Common";
import {bind} from "../util/Bind";
import {UiComponent} from "../UiComponent";
import {UiToolbar} from "../UiToolbar";
import {createUiWorkSpaceLayoutSplitItemConfig, UiWorkSpaceLayoutSplitItemConfig} from "../../generated/UiWorkSpaceLayoutSplitItemConfig";
import {UiWorkSpaceLayoutItemConfig} from "../../generated/UiWorkSpaceLayoutItemConfig";
import {createUiWorkSpaceLayoutViewItemConfig, UiWorkSpaceLayoutViewItemConfig} from "../../generated/UiWorkSpaceLayoutViewItemConfig";
import {EventFactory} from "../../generated/EventFactory";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {SplitPaneItem} from "./SplitPaneItem";
import {View} from "./View";
import {ItemTree, ItemTreeItem} from "./ItemTree";
import {TabPanelItem, TabPanelItemState} from "./TabPanelItem";
import {ViewContainer, ViewContainerListener} from "./ViewContainer";
import {RelativeDropPosition} from "./RelativeDropPosition";
import {WindowLayoutDescriptor} from "./WindowLayoutDescriptor";

export class LocalViewContainer implements ViewContainer {

	private static logger: log.Logger = log.getLogger("LocalViewContainer");
	private static DND_MIME_TYPE: string = getMicrosoftBrowserVersion() ? 'text' : 'teamapps/uiworkspaceview';

	private itemTree = new ItemTree();
	private _toolbar: UiToolbar;

	private $mainDiv: JQuery;
	private $toolbarContainer: JQuery;
	private $contentContainer: JQuery;
	private $tmpContainer: JQuery;
	private $dndActiveRectangle: JQuery;
	private $dndImage: JQuery;
	private lastDndEventType: string;
	private _attachedToDom: boolean = false;

	private $maximizationContainerWrapper: JQuery;
	private $maximizationContainer: JQuery;
	private $normalContainerOfMaximizedTabPanel: JQuery;
	private $minimizedViewsBar: JQuery;
	private viewEventsSuppressed: boolean;

	constructor(private workSpaceLayout: UiWorkSpaceLayout,
	            public readonly windowId: string,
	            viewConfigs: UiWorkSpaceLayoutViewConfig[],
	            initialLayout: UiWorkSpaceLayoutItemConfig,
	            private context: TeamAppsUiContext,
	            private listener: ViewContainerListener) {
		this.$mainDiv = $(`<div data-id="${this.workSpaceLayoutId}" class="UiWorkSpaceLayout">
    <div class="toolbar-container"></div>
    <div class="content-container-wrapper">
	    <div class="content-container"></div>
	    <div class="tmp-container"></div>
		<div class="dnd-target-rectangle"></div>
		<div class="dnd-drag-image"></div>
	</div>
	<div class="minimized-tabpanel-bar"></div>
</div>`);

		this.$toolbarContainer = this.$mainDiv.find('.toolbar-container');
		this.$contentContainer = this.$mainDiv.find('.content-container');
		this.$tmpContainer = this.$mainDiv.find('.tmp-container');
		this.$dndActiveRectangle = this.$mainDiv.find('.dnd-target-rectangle');
		this.$dndImage = this.$mainDiv.find('.dnd-drag-image');
		this.$minimizedViewsBar = this.$mainDiv.find('.minimized-tabpanel-bar');

		this.$maximizationContainerWrapper = $(`<div class="UiWorkSpaceLayout-maximization-container-wrapper"><div class="UiWorkSpaceLayout-maximization-container"></div></div>`).appendTo(document.body);
		this.$maximizationContainer = this.$maximizationContainerWrapper.find('.UiWorkSpaceLayout-maximization-container');

		let rootItem: ItemTreeItem<UiComponent<UiComponentConfig>>;
		if (initialLayout) {
			let viewByName = viewConfigs.reduce((viewsByName, viewConfig) => {
				let component = this.createViewComponent(viewConfig);
				viewsByName[viewConfig.viewName] = new View(viewConfig.viewName, viewConfig.tabIcon, viewConfig.tabCaption, viewConfig.tabCloseable, viewConfig.lazyLoading, component);
				return viewsByName;
			}, {} as {[viewName: string]: View});
			rootItem = this.createTreeItemFromLayoutDescriptor(initialLayout, viewByName);
		} else {
			rootItem = this.createTabPanelItem(null);
		}
		this.setRootItem(rootItem);
		this.fireViewNeedsRefreshForAllEmptyVisibleLazyTabs();

		let srcTabPanel = null;

		this.$mainDiv[0].addEventListener('dragstart', (e) => {
			this.lastDndEventType = 'dragstart';

			let target = e.target as HTMLElement;

			const matchingTabPanel = this.findParentTabPanel(target);

			if (matchingTabPanel) {
				let viewName = this.getViewNameForDragTarget(target);
				if (viewName) {
					let view = this.itemTree.getViewByName(viewName);
					srcTabPanel = matchingTabPanel;
					try {
						let $tabButton = matchingTabPanel.component.getMainDomElement().find(`.tab-button[data-tab-name=${viewName}]`)[0];
						if ($tabButton) {
							this.$dndImage[0].innerHTML = $tabButton.outerHTML;
							this.$dndImage.find('.tab-button').removeClass('hidden');
							e.dataTransfer.setDragImage(this.$dndImage[0], 0, 0);
						}
					} catch (e) {
						// microsoft browsers do not support this...
					}
					e.dataTransfer.effectAllowed = 'move';
					e.dataTransfer.setData(LocalViewContainer.DND_MIME_TYPE, JSON.stringify(<UiWorkspaceLayoutDndDataTransfer> {
						sourceUiSessionId: this.context.sessionId,
						sourceWorkspaceLayoutId: this.workSpaceLayoutId,
						sourceWindowId: this.windowId,
						viewName: viewName,
						tabIcon: view.tabIcon,
						tabCaption: view.tabCaption,
						tabCloseable: view.tabCloseable
					}));
				}
			}
		});
		this.$mainDiv[0].addEventListener('dragenter', (e) => {
			this.lastDndEventType = 'dragenter';
		});
		this.$mainDiv[0].addEventListener('dragover', (e) => {
			this.lastDndEventType = 'dragover';

			let dropPosition = this.determineDropPosition(e);

			if (dropPosition) {
				e.dataTransfer.dropEffect = 'move';
				this.$dndActiveRectangle.show();

				if (dropPosition.tabPanel) {
					let $tabPanelContentWrapper = dropPosition.tabPanel.component.getMainDomElement().find('.tabpanel-content-wrapper');
					let tabPanelContentRect = $tabPanelContentWrapper[0].getBoundingClientRect();

					if (dropPosition.relativeDropPosition === RelativeDropPosition.TAB) {
						this.$dndActiveRectangle.position({
							my: "left top",
							at: "left top",
							of: $tabPanelContentWrapper
						});
						this.$dndActiveRectangle.css({width: tabPanelContentRect.width, height: tabPanelContentRect.height});
					} else if (dropPosition.relativeDropPosition === RelativeDropPosition.LEFT) {
						this.$dndActiveRectangle.position({
							my: "left top",
							at: "left top",
							of: $tabPanelContentWrapper
						});
						this.$dndActiveRectangle.css({width: tabPanelContentRect.width / 2, height: tabPanelContentRect.height});
					} else if (dropPosition.relativeDropPosition === RelativeDropPosition.RIGHT) {
						this.$dndActiveRectangle.position({
							my: "right top",
							at: "right top",
							of: $tabPanelContentWrapper
						});
						this.$dndActiveRectangle.css({width: tabPanelContentRect.width / 2, height: tabPanelContentRect.height});
					} else if (dropPosition.relativeDropPosition === RelativeDropPosition.TOP) {
						this.$dndActiveRectangle.position({
							my: "left top",
							at: "left top",
							of: $tabPanelContentWrapper
						});
						this.$dndActiveRectangle.css({width: tabPanelContentRect.width, height: tabPanelContentRect.height / 2});
					} else if (dropPosition.relativeDropPosition === RelativeDropPosition.BOTTOM) {
						this.$dndActiveRectangle.position({
							my: "left bottom",
							at: "left bottom",
							of: $tabPanelContentWrapper
						});
						this.$dndActiveRectangle.css({width: tabPanelContentRect.width, height: tabPanelContentRect.height / 2});
					}
				} else {
					let $workSpaceLayout = this.$contentContainer;
					let workSpaceLayoutRect = $workSpaceLayout[0].getBoundingClientRect();

					if (dropPosition.relativeDropPosition === RelativeDropPosition.LEFT) {
						this.$dndActiveRectangle.position({
							my: "left top",
							at: "left top",
							of: $workSpaceLayout
						});
						this.$dndActiveRectangle.css({width: workSpaceLayoutRect.width / 3, height: workSpaceLayoutRect.height});
					} else if (dropPosition.relativeDropPosition === RelativeDropPosition.RIGHT) {
						this.$dndActiveRectangle.position({
							my: "right top",
							at: "right top",
							of: $workSpaceLayout
						});
						this.$dndActiveRectangle.css({width: workSpaceLayoutRect.width / 3, height: workSpaceLayoutRect.height});
					} else if (dropPosition.relativeDropPosition === RelativeDropPosition.TOP) {
						this.$dndActiveRectangle.position({
							my: "left top",
							at: "left top",
							of: $workSpaceLayout
						});
						this.$dndActiveRectangle.css({width: workSpaceLayoutRect.width, height: workSpaceLayoutRect.height / 3});
					} else if (dropPosition.relativeDropPosition === RelativeDropPosition.BOTTOM) {
						this.$dndActiveRectangle.position({
							my: "left bottom",
							at: "left bottom",
							of: $workSpaceLayout
						});
						this.$dndActiveRectangle.css({width: workSpaceLayoutRect.width, height: workSpaceLayoutRect.height / 3});
					}
				}


			} else {
				this.$dndActiveRectangle.hide();
			}

			if (e.preventDefault) {
				e.preventDefault(); // Necessary. Allows us to drop.
			}
			return false;
		});
		this.$mainDiv[0].addEventListener('dragleave', (e) => {
			this.lastDndEventType = 'dragleave';
			this.$dndActiveRectangle.hide();
		});
		this.$mainDiv[0].addEventListener('drop', (e) => {
			this.$dndActiveRectangle.hide();

			let dropPosition = this.determineDropPosition(e);
			let dataTransferString = e.dataTransfer.getData(LocalViewContainer.DND_MIME_TYPE);
			if (dropPosition && dataTransferString != null) {
				let dataTransfer = JSON.parse(dataTransferString) as UiWorkspaceLayoutDndDataTransfer;
				if (this.context.sessionId === dataTransfer.sourceUiSessionId && dataTransfer.sourceWorkspaceLayoutId === this.workSpaceLayoutId) {
					if (dataTransfer.sourceWindowId === this.windowId) {
						if (dropPosition.tabPanel) {
							if (dropPosition.relativeDropPosition === RelativeDropPosition.TAB) {
								this.moveViewToTab(dataTransfer.viewName, dropPosition.tabPanel.tabs[0].viewName);
							} else {
								let uiRelativeWorkSpaceViewPosition = UiRelativeWorkSpaceViewPosition[RelativeDropPosition[dropPosition.relativeDropPosition] as keyof typeof UiRelativeWorkSpaceViewPosition];
								this.moveViewRelativeToOtherView(dataTransfer.viewName, dropPosition.tabPanel.tabs[0].viewName, uiRelativeWorkSpaceViewPosition, UiSplitSizePolicy.RELATIVE, .5);
							}
						} else {
							let uiRelativeWorkSpaceViewPosition = UiRelativeWorkSpaceViewPosition[RelativeDropPosition[dropPosition.relativeDropPosition] as keyof typeof UiRelativeWorkSpaceViewPosition];
							let isFirst = dropPosition.relativeDropPosition === RelativeDropPosition.LEFT || dropPosition.relativeDropPosition === RelativeDropPosition.TOP;
							this.moveViewToTopLevel(dataTransfer.viewName, this.windowId, uiRelativeWorkSpaceViewPosition, UiSplitSizePolicy.RELATIVE, isFirst ? .3 : .7);
						}
						this.listener.handleLocalLayoutChangedByUser(this.windowId);
					} else {
						if (this.workSpaceLayoutId === dataTransfer.sourceWorkspaceLayoutId) {
							this.listener.handleViewDroppedFromOtherWindow(dataTransfer.sourceWindowId, this.windowId, {
								viewName: dataTransfer.viewName,
								tabIcon: dataTransfer.tabIcon,
								tabCaption: dataTransfer.tabCaption,
								tabCloseable: dataTransfer.tabCloseable,
							}, dropPosition.tabPanel && dropPosition.tabPanel.tabs[0].viewName, dropPosition.relativeDropPosition);
						}
					}
				} else {
					LocalViewContainer.logger.warn("The user dropped a view from another UiWorkSpaceLayout. Not accepting this.");
					return true;
				}
				return false;
			}
		}, false);
		this.$mainDiv[0].addEventListener('dragend', (e: DragEvent) => {
			this.$dndActiveRectangle.hide();
			const dropEffect = e.dataTransfer.dropEffect;
			const target = e.target as HTMLElement;
			const viewName = this.getViewNameForDragTarget(target);
			const dropSuccessful = dropEffect === 'move';
			let droppedOutsideWorkSpaceLayout = this.lastDndEventType === 'dragleave';
			if (droppedOutsideWorkSpaceLayout && !dropSuccessful) {
				this.createSubWindow(viewName);
			} else if (droppedOutsideWorkSpaceLayout && dropSuccessful) {
				// The other window will request a view refresh. This component will intercept it and register the windowDelegate...
			} else {
				// Nothing to do. The move is made in the 'drop' handler.
			}
			this.lastDndEventType = 'dragend';
		}, false);
	}

	private createViewComponent(viewConfig: UiWorkSpaceLayoutViewConfig) {
		return viewConfig.component ? this.context.createComponent(viewConfig.component) : null;
	}

	private get workSpaceLayoutId() {
		return this.workSpaceLayout.getId();
	}

	public setToolbar(toolbar: UiToolbarConfig): void {
		if (this._toolbar) {
			this.$toolbarContainer.empty();
			this._toolbar.destroy();
			this._toolbar = null;
		}
		if (toolbar) {
			this._toolbar = <UiToolbar> this.context.createComponent(toolbar);
			this._toolbar.getMainDomElement().appendTo(this.$toolbarContainer);
			this._toolbar.attachedToDom = this.attachedToDom;
			this._toolbar.onEmptyStateChanged.addListener(() => this.updateToolbarVisibility());
		}
		this.updateToolbarVisibility();
	}

	private updateToolbarVisibility() {
		this.$toolbarContainer.toggleClass('hidden', this._toolbar == null || this._toolbar.empty);
		if (this._toolbar) {
			this._toolbar.reLayout();
		}
	}

	public getViewInfo(viewName: string) {
		return this.itemTree.getViewByName(viewName).viewInfo;
	}

	get viewNames() {
		return this.itemTree.viewNames;
	}

	private createSubWindow(viewName: string | null) {
		let childWindowId = generateUUID();
		let childWindow = window.open("index.html" + (location.search ? location.search + "&" : "?") + "teamAppsContext=UiWorkSpaceLayoutChildWindowTeamAppsUiContext", childWindowId, "height=600,width=800,location=0");

		if (!childWindow || childWindow.closed || typeof childWindow.closed === 'undefined') {
			LocalViewContainer.logger.warn("Popup window was blocked.");
			this.listener.handleChildWindowCreationFailed(viewName);
		} else {
			let onWindowLoaded = () => {
				const channel = new MessageChannel();

				this.listener.handleChildWindowCreated(childWindowId, channel.port1, this.itemTree.getViewByName(viewName).viewInfo);

				childWindow.postMessage("", location.origin, [channel.port2]);
			};

			if (getMicrosoftBrowserVersion()) {
				// IE and Edge only return from the window.open() method _after_ the page has been loaded
				onWindowLoaded();
			} else {
				childWindow.addEventListener('load', () => {
					onWindowLoaded();
				});
			}
		}
	}

	private getViewNameForDragTarget(target: HTMLElement) {
		let viewName: string | null;
		let isDraggablePanelHeadingElement = UiPanel.isDraggablePanelHeadingElement(target);
		if (isDraggablePanelHeadingElement) {
			viewName = target.closest('.tab-content-wrapper').getAttribute('data-tab-name');
		}
		let isTabButton = target.classList.contains('tab-button');
		if (isTabButton) {
			viewName = target.getAttribute('data-tab-name');
		}
		return viewName;
	}

	private determineDropPosition(e: MouseEvent): { tabPanel?: TabPanelItem, relativeDropPosition: RelativeDropPosition } {
		let workSpaceLayoutRect = this.$contentContainer[0].getBoundingClientRect();

		if (e.pageY - workSpaceLayoutRect.top < 12) {
			return {relativeDropPosition: RelativeDropPosition.TOP};
		} else if ((workSpaceLayoutRect.left + workSpaceLayoutRect.width) - e.pageX < 12) {
			return {relativeDropPosition: RelativeDropPosition.RIGHT};
		} else if ((workSpaceLayoutRect.top + workSpaceLayoutRect.height) - e.pageY < 12) {
			return {relativeDropPosition: RelativeDropPosition.BOTTOM};
		} else if (e.pageX - workSpaceLayoutRect.left < 12) {
			return {relativeDropPosition: RelativeDropPosition.LEFT};
		}

		const matchingTabPanel = this.findParentTabPanel(e.target as HTMLElement);

		if (matchingTabPanel != null) {
			let view = matchingTabPanel.tabs[0];
			let tabPanelContentWrapper = matchingTabPanel.component.getMainDomElement().find('.tabpanel-content-wrapper');
			let tabPanelContentRect = tabPanelContentWrapper[0].getBoundingClientRect();
			const relativeEventX = (e.pageX - tabPanelContentRect.left) / tabPanelContentRect.width;
			const relativeEventY = (e.pageY - tabPanelContentRect.top) / tabPanelContentRect.height;

			if (relativeEventY < 0 || e.pageY - tabPanelContentRect.top < 30) {
				return {tabPanel: matchingTabPanel, relativeDropPosition: RelativeDropPosition.TAB};
			} else if (relativeEventX > 0.35 && relativeEventX < 0.65 && relativeEventY > 0.35 && relativeEventY < 0.65) {
				return {tabPanel: matchingTabPanel, relativeDropPosition: RelativeDropPosition.TAB};
			} else if (relativeEventX < 0.5 && relativeEventY > relativeEventX && (1 - relativeEventY) > relativeEventX) {
				return {tabPanel: matchingTabPanel, relativeDropPosition: RelativeDropPosition.LEFT};
			} else if (relativeEventX > 0.5 && relativeEventY < relativeEventX && (1 - relativeEventY) < relativeEventX) {
				return {tabPanel: matchingTabPanel, relativeDropPosition: RelativeDropPosition.RIGHT};
			} else if (relativeEventY < 0.5 && relativeEventY < relativeEventX && relativeEventY < (1 - relativeEventX)) {
				return {tabPanel: matchingTabPanel, relativeDropPosition: RelativeDropPosition.TOP}
			} else { //if (relativeEventY > 0.5 && relativeEventY > relativeEventX && relativeEventY > (1 - relativeEventX)) {
				return {tabPanel: matchingTabPanel, relativeDropPosition: RelativeDropPosition.BOTTOM}
			}
		} else {
			return null;
		}
	}

	private findParentTabPanel(target: HTMLElement): TabPanelItem {
		let allTabPanelItems = this.itemTree.getAllTabPanelItems();
		let matchingTabPanel: TabPanelItem;
		while (target != null && target != this.$mainDiv[0]) {
			matchingTabPanel = allTabPanelItems.filter(item => item.component.getMainDomElement()[0] === target)[0];
			if (matchingTabPanel) {
				break;
			} else {
				target = target.parentElement;
			}
		}
		return matchingTabPanel;
	}

	private setRootItem(item: ItemTreeItem<UiComponent<UiComponentConfig>>): void {
		if (this.itemTree.rootItem != null && isEmptyable(this.itemTree.rootItem.component)) {
			this.itemTree.rootItem.component.onEmptyStateChanged.removeListener(this.onRootItemEmptyStateChanged);
		}
		this.itemTree.rootItem = item;
		item.component.getMainDomElement().appendTo(this.$contentContainer);
		item.component.attachedToDom = this.attachedToDom;
		if (isEmptyable(item.component)) {
			item.component.onEmptyStateChanged.addListener(this.onRootItemEmptyStateChanged);
			this.onRootItemEmptyStateChanged(item.component.empty);
		}
	}

	@bind
	private onRootItemEmptyStateChanged(empty: boolean) {
		this.$contentContainer.toggleClass("hidden", empty)
	}

	private createTreeItemFromLayoutDescriptor(descriptor: UiWorkSpaceLayoutItemConfig, viewsByName: { [viewName: string]: View }): ItemTreeItem<UiComponent<UiComponentConfig>> {
		let createTreeItem = (descriptor: UiWorkSpaceLayoutItemConfig, parent: SplitPaneItem, viewsByName: { [viewName: string]: View }) => {
			if (LocalViewContainer.isTabPanelDescriptor(descriptor)) {
				let tabPanelItem = this.createTabPanelItem(parent);
				descriptor.viewNames.forEach((viewName, index) => {
					let view = viewsByName[viewName];
					if (view == null) {
						LocalViewContainer.logger.error("View item references non-existing view: " + viewName)
					}
					let selected = viewName === descriptor.selectedViewName || descriptor.selectedViewName == null && index === 0;
					tabPanelItem.addTab(view, selected);
				});
				if (descriptor.minimized) {
					this.minimizeTabPanel(tabPanelItem);
				}
				return tabPanelItem;
			} else if (LocalViewContainer.isSplitPanelDescriptor(descriptor)) {
				let splitPaneItem = new SplitPaneItem(parent, descriptor.splitDirection, descriptor.sizePolicy, descriptor.referenceChildSize, this.context);
				let firstChild: ItemTreeItem<UiComponent<UiComponentConfig>>;
				if (descriptor.firstChild) {
					firstChild = createTreeItem(descriptor.firstChild, splitPaneItem, viewsByName);
					firstChild.parent = splitPaneItem;
					splitPaneItem.firstChild = firstChild;
				}
				let lastChild: ItemTreeItem<UiComponent<UiComponentConfig>>;
				if (descriptor.lastChild) {
					lastChild = createTreeItem(descriptor.lastChild, splitPaneItem, viewsByName);
					lastChild.parent = splitPaneItem;
					splitPaneItem.lastChild = lastChild;
				}
				return splitPaneItem;
			}
		};
		let splitPaneItem: TabPanelItem | SplitPaneItem;
		try {
			this.viewEventsSuppressed = true;
			splitPaneItem = createTreeItem(descriptor, null, viewsByName);
		} finally {
			this.viewEventsSuppressed = false;
		}
		return splitPaneItem;
	}

	private createTabPanelItem(parent: SplitPaneItem) {
		let tabPanelItem = new TabPanelItem(parent, this.context);
		tabPanelItem.onTabSelected.addListener(this.tabSelected);
		tabPanelItem.onTabNeedsRefresh.addListener(this.tabNeedsRefresh);
		tabPanelItem.onMinimizeButtonPressed.addListener(this.minimizeTabPanel);
		tabPanelItem.onMaximizeButtonPressed.addListener(this.maximizeTabPanel);
		tabPanelItem.onRestoreButtonPressed.addListener(this.restoreTabPanel);
		tabPanelItem.onTabClosed.addListener(tabId => {
			if (this.itemTree.getViewByName(tabId).parent.tabs.length === 0) {
				this.restoreTabPanel(this.itemTree.getViewByName(tabId).parent);
			}
			this.workSpaceLayout.onViewTabClosed.fire(EventFactory.createUiWorkSpaceLayout_ViewTabClosedEvent(this.workSpaceLayoutId, tabId));
		});
		return tabPanelItem;
	}

	@bind
	private tabSelected(eventObject: { tabId: string }, tabPanelItem: TabPanelItem) {
		let viewName = eventObject.tabId;
		let otherViewNames = tabPanelItem.tabs.map(tab => tab.viewName).filter(otherViewName => otherViewName !== viewName);
		if (!this.viewEventsSuppressed) {
			this.workSpaceLayout.onViewTabSelected.fire(EventFactory.createUiWorkSpaceLayout_ViewTabSelectedEvent(this.workSpaceLayoutId, viewName, otherViewNames));
		}
	}

	@bind
	private tabNeedsRefresh(eventObject: { tabId: string }) {
		if (!this.viewEventsSuppressed) {
			this.workSpaceLayout.onViewNeedsRefresh.fire(EventFactory.createUiWorkSpaceLayout_ViewNeedsRefreshEvent(this.workSpaceLayoutId, eventObject.tabId));
		}
	}

	@bind
	private minimizeTabPanel(tabPanelItem: TabPanelItem) {
		if (tabPanelItem.maximized) {
			this.restoreTabPanel(tabPanelItem);
		}
		tabPanelItem.state = TabPanelItemState.MINIMIZED;
		this.$minimizedViewsBar.append(tabPanelItem.$minimizedButton);
		this.reLayout();
	}

	@bind
	private maximizeTabPanel(tabPanelItem: TabPanelItem) {
		const $element = tabPanelItem.component.getMainDomElement();
		this.$normalContainerOfMaximizedTabPanel = $element.parent();
		this.$maximizationContainerWrapper.addClass("show");
		this.$maximizationContainer.append($element).addClass("animated zoomIn");
		tabPanelItem.component.onResize();
		tabPanelItem.state = TabPanelItemState.MAXIMIZED;
	}

	@bind
	private restoreTabPanel(tabPanelItem: TabPanelItem) {
		if (tabPanelItem.state === TabPanelItemState.MAXIMIZED) {
			const $element = tabPanelItem.component.getMainDomElement();
			this.$maximizationContainerWrapper.removeClass("show");
			this.$maximizationContainer.append($element).removeClass("animated zoomIn");
			$element.appendTo(this.$normalContainerOfMaximizedTabPanel);
			tabPanelItem.state = TabPanelItemState.NORMAL;
			tabPanelItem.component.onResize();
		} else if (tabPanelItem.state === TabPanelItemState.MINIMIZED) {
			tabPanelItem.$minimizedButton.remove();
			tabPanelItem.state = TabPanelItemState.NORMAL;
			tabPanelItem.component.onResize();
		}
	}

	private static isTabPanelDescriptor(item: UiWorkSpaceLayoutItemConfig): item is UiWorkSpaceLayoutViewItemConfig {
		return item._type === 'UiWorkSpaceLayoutViewItem';
	}

	private static isSplitPanelDescriptor(item: UiWorkSpaceLayoutItemConfig): item is UiWorkSpaceLayoutSplitItemConfig {
		return item._type === 'UiWorkSpaceLayoutSplitItem';
	}

	addViewToTopLevel(newViewConfig: UiWorkSpaceLayoutViewConfig, windowId: string, relativePosition: UiRelativeWorkSpaceViewPosition, sizePolicy: UiSplitSizePolicy, referenceChildSize: number): void {
		// windowId can be ignored here, since this method is only invoked if this is the target window!
		let component = this.createViewComponent(newViewConfig);
		let view = new View(newViewConfig.viewName, newViewConfig.tabIcon, newViewConfig.tabCaption, newViewConfig.tabCloseable, newViewConfig.lazyLoading, component);
		this.addViewItemToNewPosition(view, null, relativePosition, sizePolicy, referenceChildSize);
	}

	addViewRelativeToOtherView(newViewConfig: UiWorkSpaceLayoutViewConfig, existingViewName: string, relativePosition: UiRelativeWorkSpaceViewPosition, sizePolicy: UiSplitSizePolicy, referenceChildSize: number): void {
		let component = this.createViewComponent(newViewConfig);
		let view = new View(newViewConfig.viewName, newViewConfig.tabIcon, newViewConfig.tabCaption, newViewConfig.tabCloseable, newViewConfig.lazyLoading, component);
		this.addViewItemToNewPosition(view, existingViewName, relativePosition, sizePolicy, referenceChildSize);
	}

	addViewAsTab(newViewConfig: UiWorkSpaceLayoutViewConfig, existingViewName: string, select: boolean): void {
		let component = this.createViewComponent(newViewConfig);
		let view = new View(newViewConfig.viewName, newViewConfig.tabIcon, newViewConfig.tabCaption, newViewConfig.tabCloseable, newViewConfig.lazyLoading, component);
		this.addViewItemAsTab(view, existingViewName, select);
	}

	private addViewItemToNewPosition(view: View, existingViewName: string, relativePosition: UiRelativeWorkSpaceViewPosition, sizePolicy: UiSplitSizePolicy, referenceChildSize: number) {
		if (existingViewName != null) {
			let siblingView: View = this.itemTree.getViewByName(existingViewName);
			let siblingTabPanelItem = siblingView.parent;
			let oldParent = siblingTabPanelItem.parent;
			let existingTabPanelIsPosition: 'ROOT' | 'FIRST' | 'LAST' = oldParent == null ? 'ROOT' : oldParent.firstChild === siblingTabPanelItem ? 'FIRST' : 'LAST';
			let newItemWillBeFirstChild = [UiRelativeWorkSpaceViewPosition.LEFT, UiRelativeWorkSpaceViewPosition.TOP].indexOf(relativePosition) !== -1;

			let isVerticalSplit = [UiRelativeWorkSpaceViewPosition.LEFT, UiRelativeWorkSpaceViewPosition.RIGHT].indexOf(relativePosition) !== -1;
			let newSplitPaneItem = new SplitPaneItem(oldParent, isVerticalSplit ? UiSplitDirection.VERTICAL : UiSplitDirection.HORIZONTAL, sizePolicy, referenceChildSize, this.context);
			let newTabPanelItem = this.createTabPanelItem(newSplitPaneItem);
			newTabPanelItem.addTab(view, true);

			if (existingTabPanelIsPosition === 'FIRST') {
				oldParent.component.firstChildComponent.getMainDomElement().appendTo(this.$tmpContainer);
				oldParent.firstChild = newSplitPaneItem;
			} else if (existingTabPanelIsPosition === 'LAST') {
				oldParent.component.lastChildComponent.getMainDomElement().appendTo(this.$tmpContainer);
				oldParent.lastChild = newSplitPaneItem;
			} else { // siblingTabPanelItem is root!
				siblingTabPanelItem.component.getMainDomElement().appendTo(this.$tmpContainer);
				this.setRootItem(newSplitPaneItem);
			}

			newSplitPaneItem.firstChild = newItemWillBeFirstChild ? newTabPanelItem : siblingTabPanelItem;
			newSplitPaneItem.lastChild = newItemWillBeFirstChild ? siblingTabPanelItem : newTabPanelItem;

			siblingTabPanelItem.parent = newSplitPaneItem
		} else {
			if (this.itemTree.viewCount === 0) {
				let rootTabPanelItem = this.itemTree.rootItem as TabPanelItem;
				rootTabPanelItem.addTab(view, true);
			} else {
				let isVerticalSplit = [UiRelativeWorkSpaceViewPosition.LEFT, UiRelativeWorkSpaceViewPosition.RIGHT].indexOf(relativePosition) !== -1;
				let splitPaneItem = new SplitPaneItem(null, isVerticalSplit ? UiSplitDirection.VERTICAL : UiSplitDirection.HORIZONTAL, sizePolicy, referenceChildSize, this.context);
				let oldRootItem = this.itemTree.rootItem;
				oldRootItem.component.getMainDomElement().appendTo(this.$tmpContainer);
				this.setRootItem(splitPaneItem);

				let newTabPanelItem = this.createTabPanelItem(splitPaneItem);
				newTabPanelItem.addTab(view, true);
				oldRootItem.parent = splitPaneItem;

				let newItemWillBeFirstChild = [UiRelativeWorkSpaceViewPosition.LEFT, UiRelativeWorkSpaceViewPosition.TOP].indexOf(relativePosition) !== -1;
				splitPaneItem.firstChild = newItemWillBeFirstChild ? newTabPanelItem : oldRootItem;
				splitPaneItem.lastChild = newItemWillBeFirstChild ? oldRootItem : newTabPanelItem;
			}
		}
		if (view.component) {
			view.component.attachedToDom = this._attachedToDom;
		}
		this.itemTree.updateIndex();
	}

	private addViewItemAsTab(view: View, existingViewName: string, select: boolean) {
		let siblingView: View = this.itemTree.getViewByName(existingViewName);
		let tabPanelItem = siblingView.parent;
		tabPanelItem.addTab(view, select);
		this.itemTree.updateIndex();
	}

	public removeView(viewName: string, destroyContentComponent = true, updateIndex = true) {
		let view = this.itemTree.getViewByName(viewName);
		if (view == null) {
			LocalViewContainer.logger.warn(`Cannot remove view ${viewName}. View not found.`);
			return;
		}
		let tabPanelItem: TabPanelItem = view.parent;
		tabPanelItem.removeTab(view, destroyContentComponent);
		if (tabPanelItem.tabs.length === 0) {
			let parentSplitPaneItem = tabPanelItem.parent;
			if (parentSplitPaneItem != null) { // tabPanelItem is the rootItem
				let tabPanelItemIsFirstChild = parentSplitPaneItem.firstChild === tabPanelItem;
				let grandParentSplitPaneItem = parentSplitPaneItem.parent;
				let siblingItem: ItemTreeItem<UiComponent<UiComponentConfig>>;
				if (tabPanelItemIsFirstChild) {
					siblingItem = parentSplitPaneItem.lastChild;
					siblingItem.component.getMainDomElement().appendTo(this.$tmpContainer);
					parentSplitPaneItem.lastChild = null;
				} else {
					siblingItem = parentSplitPaneItem.firstChild;
					siblingItem.component.getMainDomElement().appendTo(this.$tmpContainer);
					parentSplitPaneItem.firstChild = null;
				}
				if (grandParentSplitPaneItem != null) {
					let parentSplitPaneItemIsFirstChild = grandParentSplitPaneItem.firstChild === parentSplitPaneItem;
					parentSplitPaneItem.component.destroy();
					if (parentSplitPaneItemIsFirstChild) {
						grandParentSplitPaneItem.firstChild = siblingItem;
					} else {
						grandParentSplitPaneItem.lastChild = siblingItem;
					}
				} else {
					parentSplitPaneItem.component.destroy();
					parentSplitPaneItem.component.getMainDomElement().remove();
					this.setRootItem(siblingItem);
					siblingItem.parent = null;
				}
			}
		}
		if (updateIndex) {
			this.itemTree.updateIndex();
		}
		if (this.windowId !== UiWorkSpaceLayout.ROOT_WINDOW_ID && this.itemTree.viewCount === 0) {
			window.close();
		}
	}

	moveViewToTopLevel(viewName: string, windowId: string, relativePosition: UiRelativeWorkSpaceViewPosition, sizePolicy: UiSplitSizePolicy, referenceChildSize: number): void {
		this.moveViewRelativeToOtherView(viewName, null, relativePosition, sizePolicy, referenceChildSize);
	}

	moveViewRelativeToOtherView(viewName: string, newSiblingName: string, relativePosition: UiRelativeWorkSpaceViewPosition, sizePolicy: UiSplitSizePolicy, referenceChildSize: number): void {
		let view = this.itemTree.getViewByName(viewName);
		if (viewName === newSiblingName && view.parent.tabs.length <= 1) {
			return; // would not have any effect anyway
		}
		view.component.getMainDomElement().appendTo(this.$tmpContainer);
		this.removeView(viewName, false, false);
		this.addViewItemToNewPosition(view, newSiblingName, relativePosition, sizePolicy, referenceChildSize);
	}

	moveViewToTab(viewName: string, newSiblingName: string): void {
		let view = this.itemTree.getViewByName(viewName);
		if (viewName === newSiblingName) {
			return; // would not have any effect anyway
		}
		view.component.getMainDomElement().appendTo(this.$tmpContainer);
		this.removeView(viewName, false, false);
		this.addViewItemAsTab(view, newSiblingName, true);
	}

	public refreshViewComponent(viewName: string, componentConfig: UiComponentConfig) {
		let view = this.itemTree.getViewByName(viewName);
		view.component = componentConfig ? this.context.createComponent(componentConfig) : null;
		if (view.component) {
			view.component.attachedToDom = this._attachedToDom;
		}
	}

	refreshView(viewName: string, componentConfig: UiComponentConfig, tabIcon: string, tabCaption: string, tabCloseable: boolean): void {
		this.refreshViewComponent(viewName, componentConfig);
		let view = this.itemTree.getViewByName(viewName);
		view.updateTabAttributes(tabIcon, tabCaption, tabCloseable);
	}

	redefineLayout(windowId: string, newLayout: UiWorkSpaceLayoutItemConfig): void {
		this.itemTree.views.forEach(view => view.component.getMainDomElement().appendTo(this.$tmpContainer));
		this.$contentContainer.empty();
		this.setRootItem(this.createTreeItemFromLayoutDescriptor(newLayout, this.itemTree.viewsByName));
		this.fireViewNeedsRefreshForAllEmptyVisibleLazyTabs();
	}

	selectViewTab(viewName: string) {
		let tabPanel = this.itemTree.getTabPanelForView(viewName);
		if (tabPanel) {
			tabPanel.selectTab(viewName);
		}
	}

	public set attachedToDom(attachedToDom: boolean) {
		let wasAttachedToDom = this._attachedToDom;
		this._attachedToDom = attachedToDom;
		if (attachedToDom && !wasAttachedToDom) {
			this.onAttachedToDom();
		}
	}

	public get attachedToDom() {
		return this._attachedToDom;
	}

	private onAttachedToDom() {
		if (this._toolbar) this._toolbar.attachedToDom = true;
		if (this.itemTree.rootItem.component) this.itemTree.rootItem.component.attachedToDom = true;
	}

	reLayout() {
		this.$contentContainer.css("overflow", "hidden"); // enforce container size (from flex layout) over children sizes!
		this._toolbar && this._toolbar.reLayout();
		this.itemTree.rootItem && this.itemTree.rootItem.component.reLayout();
		this.$contentContainer.css("overflow", "visible");
	}

	destroy() {
		if (this._toolbar) this._toolbar.destroy();
		if (this.itemTree.rootItem.component) this.itemTree.rootItem.component.destroy();
		this.$maximizationContainerWrapper.remove();
	}

	getMainDomElement() {
		return this.$mainDiv;
	}

	private createLayoutDescriptor(item: ItemTreeItem<UiComponent<UiComponentConfig>>): UiWorkSpaceLayoutItemConfig {
		if (item instanceof SplitPaneItem) {
			return createUiWorkSpaceLayoutSplitItemConfig(item.splitDirection, this.createLayoutDescriptor(item.firstChild), this.createLayoutDescriptor(item.lastChild), {
				sizePolicy: item.sizePolicy,
				referenceChildSize: item.referenceChildSize
			})
		} else if (item instanceof TabPanelItem) {
			return createUiWorkSpaceLayoutViewItemConfig(item.tabs.map(view => view.viewName), {
				selectedViewName: item.component.getSelectedTabId(),
				minimized: item.state === TabPanelItemState.MINIMIZED
			});
		}
	}

	async getLayoutDescriptor(): Promise<WindowLayoutDescriptor> {
		return {
			windowId: this.windowId,
			layout: this.createLayoutDescriptor(this.itemTree.rootItem)
		};
	}

	private fireViewNeedsRefreshForAllEmptyVisibleLazyTabs() {
		this.itemTree.getAllTabPanelItems()
			.forEach(tabPanelItem => {
				let selectedTabId: string = tabPanelItem.component.getSelectedTabId();
				if (selectedTabId != null) { // might be completely empty!
					let selectedView: View = tabPanelItem.tabs.filter(tab => tab.viewName === selectedTabId)[0];
					if (selectedView.component == null && selectedView.lazyLoading) {
						this.workSpaceLayout.onViewNeedsRefresh.fire(EventFactory.createUiWorkSpaceLayout_ViewNeedsRefreshEvent(this.workSpaceLayoutId, selectedTabId));
					}
				}
			});
	}
}
